import { type PostHog } from 'posthog-js';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';

import { config } from '@/config';

export const FeatureFlags = {
  CRYPTO_WALLET: 'crypto_wallet',
  MAINTENANCE: 'maintenance',
  INVESTING_FLOW: 'investing_flow',
  GAMMA_EVENTS_ENABLED: 'gamma_events_enabled',
  WALLET_RECOVERY: 'wallet_recovery',
  WALLET_MFA_ENROLLMENT: 'wallet_mfa_enrollment',
  PASSKEYS_ENABLED: 'passkeys_enabled',
  USER_PROFILE_V2: 'user_profile_v2',
  GOOGLE_SIGN_IN_ENABLED: 'web_google_sign_in_enabled',
  APPLE_SIGN_IN_ENABLED: 'web_apple_sign_in_enabled',
  PREIPO_V2_ADMIN: 'preipo_v2_admin',
  PREIPO_V2_ENTITY: 'preipo_v2_entity',
} as const;

export type FeatureFlagName = (typeof FeatureFlags)[keyof typeof FeatureFlags];

const envFlagName = (flag: FeatureFlagName) =>
  `${config.IS_DEVELOPMENT ? 'development' : 'production'}_${flag}`;

/**
 * Comma-separated `flag=true|false`, keyed by the bare flag name. Anything not matching is
 * ignored rather than coerced, so a typo cannot read as `false` and close a live feature.
 */
const parseFlagMap = (raw = ''): Readonly<Record<string, boolean>> =>
  Object.freeze(
    Object.fromEntries(
      raw.split(',').flatMap((entry) => {
        const match = /^\s*([^=\s]+)\s*=\s*(true|false)\s*$/i.exec(entry);
        return match ? [[match[1], match[2].toLowerCase() === 'true'] as const] : [];
      }),
    ),
  );

/** Short-circuits PostHog entirely. Forces a value even when PostHog answers, which is how the
 * closed state of a live flag is exercised locally. Not a default — see `FLAG_POLICY`. */
const overrides = parseFlagMap(config.VITE_FEATURE_FLAG_OVERRIDES);

/**
 * True when there is no PostHog to ask, so every flag stays `undefined` forever. That is the
 * normal local setup — no `VITE_PUBLIC_POSTHOG_KEY` — and it would otherwise hide every flagged
 * feature from the people building it.
 */
const isFlagUnanswerable = (): boolean => config.IS_DEVELOPMENT && !config.posthog.apiKey;

/**
 * Route timeout fallback (`unanswered`) and local defaults when PostHog is not configured.
 * Being a Record makes a new flag a compile error
 * until its policy is stated.
 */
const FLAG_POLICY: Record<FeatureFlagName, { unanswered: boolean; local: boolean }> = {
  [FeatureFlags.GOOGLE_SIGN_IN_ENABLED]: { unanswered: false, local: false },
  [FeatureFlags.APPLE_SIGN_IN_ENABLED]: { unanswered: false, local: false },
  [FeatureFlags.CRYPTO_WALLET]: { unanswered: false, local: true },
  [FeatureFlags.INVESTING_FLOW]: { unanswered: false, local: true },
  [FeatureFlags.GAMMA_EVENTS_ENABLED]: { unanswered: false, local: true },
  [FeatureFlags.WALLET_RECOVERY]: { unanswered: false, local: false },
  [FeatureFlags.WALLET_MFA_ENROLLMENT]: { unanswered: false, local: true },
  [FeatureFlags.PASSKEYS_ENABLED]: { unanswered: false, local: true },
  // Unreleased: closed everywhere until PostHog says otherwise, open locally so the
  // people building it can see it without a PostHog key.
  [FeatureFlags.USER_PROFILE_V2]: { unanswered: false, local: true },
  [FeatureFlags.PREIPO_V2_ADMIN]: { unanswered: false, local: true },
  [FeatureFlags.PREIPO_V2_ENTITY]: { unanswered: false, local: true },
  // A behaviour switch, not a feature: "on" puts the app behind a maintenance page.
  [FeatureFlags.MAINTENANCE]: { unanswered: false, local: false },
};

/** Resolves an already-fetched PostHog answer against the flag's policy. */
const resolveFlag = (flag: FeatureFlagName, state: boolean | undefined): boolean =>
  state ?? (isFlagUnanswerable() ? FLAG_POLICY[flag].local : FLAG_POLICY[flag].unanswered);

function createFeatureFlagStore(posthog: PostHog, key: string) {
  let confirmedMissing = false;
  let reloading = false;
  const getSnapshot = () =>
    posthog.getFeatureFlagResult(key, { send_event: false })?.enabled ??
    (confirmedMissing ? false : undefined);

  return {
    getSnapshot,
    subscribe: (onChange: () => void) => {
      const unsubscribeFlags = posthog.onFeatureFlags((_flags, _variants, context) => {
        // Cross-tab cache changes can omit a flag after hasLoadedFlags becomes true.
        // Only a successful response confirms that an absent flag was disabled.
        confirmedMissing = context?.errorsLoading === false;
        reloading = false;
        onChange();
      });
      const unsubscribeReload = posthog.on('featureFlagsReloading', () => {
        confirmedMissing = false;
        reloading = true;
        onChange();
      });

      return () => {
        unsubscribeFlags();
        unsubscribeReload();
      };
    },
    reloadIfUnresolved: () => {
      // Subscription replay has no response context, so an initially absent flag needs a fresh answer.
      if (getSnapshot() === undefined && !reloading) {
        posthog.reloadFeatureFlags();
      }
    },
  };
}

export const useFeatureFlag = (flag: FeatureFlagName): boolean | undefined => {
  const posthog = usePostHog();
  const key = envFlagName(flag);
  const override = overrides[flag];
  const localDefault = isFlagUnanswerable() ? FLAG_POLICY[flag].local : undefined;
  const store = useMemo(() => createFeatureFlagStore(posthog, key), [posthog, key]);
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot);

  useEffect(() => {
    posthog.getFeatureFlagResult(key);
    if (override === undefined && localDefault === undefined) {
      store.reloadIfUnresolved();
    }
  }, [key, posthog, override, localDefault, state, store]);

  return override ?? state ?? localDefault;
};

const FLAG_RESOLVE_TIMEOUT_MS = 3000;

export type FeatureFlagGateState = 'loading' | 'on' | 'off';

export const useFeatureFlagGate = (flag: FeatureFlagName): FeatureFlagGateState => {
  const state = useFeatureFlag(flag);
  const canResolve = Boolean(config.posthog.apiKey);
  const worthWaitingFor = !FLAG_POLICY[flag].unanswered;
  const [gaveUpWaiting, setGaveUpWaiting] = useState(false);

  useEffect(() => {
    setGaveUpWaiting(false);
    if (state !== undefined || !canResolve || !worthWaitingFor) return;

    const timer = setTimeout(() => setGaveUpWaiting(true), FLAG_RESOLVE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [state, canResolve, worthWaitingFor]);

  if (state === undefined && canResolve && worthWaitingFor && !gaveUpWaiting) return 'loading';

  return resolveFlag(flag, state) ? 'on' : 'off';
};

/**
 * Every flag's resolved value. Written out rather than looped so the hook count never varies, and
 * typed as a Record so a new flag is a compile error until it is listed here.
 */
const useAllFeatureFlags = (): Record<FeatureFlagName, boolean | undefined> => ({
  [FeatureFlags.GOOGLE_SIGN_IN_ENABLED]: useFeatureFlag(FeatureFlags.GOOGLE_SIGN_IN_ENABLED),
  [FeatureFlags.APPLE_SIGN_IN_ENABLED]: useFeatureFlag(FeatureFlags.APPLE_SIGN_IN_ENABLED),
  [FeatureFlags.CRYPTO_WALLET]: useFeatureFlag(FeatureFlags.CRYPTO_WALLET),
  [FeatureFlags.MAINTENANCE]: useFeatureFlag(FeatureFlags.MAINTENANCE),
  [FeatureFlags.INVESTING_FLOW]: useFeatureFlag(FeatureFlags.INVESTING_FLOW),
  [FeatureFlags.GAMMA_EVENTS_ENABLED]: useFeatureFlag(FeatureFlags.GAMMA_EVENTS_ENABLED),
  [FeatureFlags.WALLET_RECOVERY]: useFeatureFlag(FeatureFlags.WALLET_RECOVERY),
  [FeatureFlags.WALLET_MFA_ENROLLMENT]: useFeatureFlag(FeatureFlags.WALLET_MFA_ENROLLMENT),
  [FeatureFlags.PASSKEYS_ENABLED]: useFeatureFlag(FeatureFlags.PASSKEYS_ENABLED),
  [FeatureFlags.USER_PROFILE_V2]: useFeatureFlag(FeatureFlags.USER_PROFILE_V2),
  [FeatureFlags.PREIPO_V2_ADMIN]: useFeatureFlag(FeatureFlags.PREIPO_V2_ADMIN),
  [FeatureFlags.PREIPO_V2_ENTITY]: useFeatureFlag(FeatureFlags.PREIPO_V2_ENTITY),
});

/**
 * Drops items whose `featureFlag` is closed; items without one always stay. Lets a list of tabs,
 * links or menu entries carry its own gating — same `featureFlag` field the route config uses —
 * instead of each call site filtering by name against a specific flag.
 */
export const useOpenFeatures = <T extends { featureFlag?: FeatureFlagName }>(
  items: readonly T[],
): T[] => {
  const flags = useAllFeatureFlags();

  return items.filter((item) => !item.featureFlag || flags[item.featureFlag]);
};
